import { DividaResource } from '@/models/resources';
import { acertoOrdenouDividasRecomendacao } from '@/modules/events/acerto';
import { DividasOrderingService } from '../../DividasOrderingService';
import { IOrdenacaoStrategy } from '../interfaces/IOrdenacaoStrategy';
import { TipoOrdenacao } from '../types/TipoOrdenacao';
import { OrdenacaoPorOrdemSalvaStrategy } from './OrdenacaoPorOrdemSalvaStrategy';

/**
 * Estratégia de ordenação por credores de maior receita
 */
export class OrdenacaoPorMaiorReceitaStrategy implements IOrdenacaoStrategy {
  private static readonly CREDORES_MAIOR_RECEITA: readonly string[] = [
    'Itau', 'Inter', 'Btg', 'acerto', 'WillBank', 'C6', 'Porto', 'Bmg',
    'Midway', 'Carrefour', 'Renner', 'Credsystem', 'Cea', 'Santander',
    'Havan', 'Arc4U', 'Recovery', 'Jeitto', 'Ativos', 'Itapeva', 'Claro', 'Omni',
    'Vivo', 'MercadoLivre', 'Neon', 'Pan', 'Digio', 'Magalu', 'CasasBahia',
    'Cogna', 'Return', 'Cielo',
  ] as const;

  private static credoresOrderMap: Map<string, number> | null = null;
  private static eventoDisparado = false;

  private ordemSalvaStrategy: OrdenacaoPorOrdemSalvaStrategy;

  constructor(orderingService: DividasOrderingService) {
    this.ordemSalvaStrategy = new OrdenacaoPorOrdemSalvaStrategy(orderingService);
  }

  ordenar(lote: DividaResource[], tipoOrdenacao: TipoOrdenacao): DividaResource[] {
    if (tipoOrdenacao !== TipoOrdenacao.MAIOR_RECEITA) {
      throw new Error(`Tipo de ordenação ${tipoOrdenacao} não suportado por esta estratégia`);
    }

    if (lote.length === 0) {
      return [];
    }

    if (!OrdenacaoPorMaiorReceitaStrategy.eventoDisparado) {
      acertoOrdenouDividasRecomendacao('maior_receita');
      OrdenacaoPorMaiorReceitaStrategy.eventoDisparado = true;
    }

    // Primeiro ordena por ordem salva
    const dividasOrdenadas = this.ordemSalvaStrategy.ordenar(lote, TipoOrdenacao.ORDEM_SALVA);
    const orderMap = OrdenacaoPorMaiorReceitaStrategy.getCredoresOrderMap();

    return [...dividasOrdenadas].sort((a, b) => {
      const credorA = OrdenacaoPorMaiorReceitaStrategy.normalizarCredor(a.credor);
      const credorB = OrdenacaoPorMaiorReceitaStrategy.normalizarCredor(b.credor);

      const indexA = credorA ? orderMap.get(credorA) : undefined;
      const indexB = credorB ? orderMap.get(credorB) : undefined;
      if (indexA !== undefined && indexB !== undefined) {
        if (indexA === indexB) {
          const dataA = a.dataOrigem?.getTime() ?? 0;
          const dataB = b.dataOrigem?.getTime() ?? 0;
          return dataB - dataA;
        }
        return indexA - indexB;
      }
      if (indexA !== undefined) {
        return -1;
      }
      if (indexB !== undefined) {
        return 1;
      }
      return 0;
    });
  }

  private static getCredoresOrderMap(): Map<string, number> {
    if (!this.credoresOrderMap) {
      this.credoresOrderMap = new Map<string, number>();
      this.CREDORES_MAIOR_RECEITA.forEach((credor, index) => {
        this.credoresOrderMap?.set(credor.toLowerCase(), index);
      });
    }
    return this.credoresOrderMap;
  }

  private static normalizarCredor(credor: string | undefined): string | undefined {
    return credor?.toLowerCase().trim();
  }

  /**
   * Reseta a flag de evento disparado (útil para testes ou quando a ordem é limpa)
   */
  static resetEventoDisparado(): void {
    OrdenacaoPorMaiorReceitaStrategy.eventoDisparado = false;
  }
}
